import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../features/auth';
import { useCompany } from '../context/CompanyContext';
import {
  teamService,
  TeamMember,
  grantsFromMatrixValue,
  type ModuleRoleGrant,
} from '../lib/teamService';
import { SettingsTabs } from '../components/settings';
import { PermissionsMatrix, type PermissionsMatrixValue } from '../features/team/PermissionsMatrix';
import { Users, UserPlus, Mail, Shield, Loader as Loader2, X, ArrowLeft, CircleAlert as AlertCircle, CircleCheck as CheckCircle2, CreditCard as Edit2, Trash2, UserX } from 'lucide-react';

export function TeamSettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  // ── Active company from global context — reacts to topbar company-switcher.
  const { activeCompanyId, activeCompany, loadingCompanies } = useCompany();

  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<any>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Invite form state — Phase 76.2.D: matrix value is a single dynamic record.
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [inviteMatrix, setInviteMatrix] = useState<PermissionsMatrixValue>({});
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Edit form state — Phase 76.2.D: matrix value is hydrated from
  // company_member_module_roles_v76 (canonical) with legacy-JSON fallback.
  const [editRole, setEditRole] = useState<'admin' | 'member'>('member');
  const [editMatrix, setEditMatrix] = useState<PermissionsMatrixValue>({});
  const [updating, setUpdating] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Re-run whenever the selected company changes (topbar switcher).
  useEffect(() => {
    if (!loadingCompanies) {
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompanyId, loadingCompanies]);

  const loadData = useCallback(async () => {
    if (!user || !activeCompanyId) return;
    
    setLoading(true);
    setError(null);

    try {
      // Use the active company directly from context — no extra DB round-trip.
      if (!activeCompany) {
        setError('Failed to load company data');
        return;
      }

      setCompany(activeCompany);

      // Get team members for the currently active company.
      const { data: membersData, error: membersError } = await teamService.getTeamMembers(
        activeCompanyId
      );
      
      if (membersError) {
        console.error('Load team members error:', membersError);
        setError('Failed to load team members');
        return;
      }

      setTeamMembers(membersData || []);
    } catch (err) {
      console.error('Load data error:', err);
      setError('An error occurred while loading data');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, activeCompanyId, activeCompany]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!company || !inviteEmail.trim()) return;

    setInviting(true);
    setInviteError(null);
    setSuccess(null);

    try {
      // Phase 76.2.D — translate the dynamic matrix value into the canonical
      // grant array. Admins do not need per-module grants (the role column
      // governs full access), so we explicitly pass an empty array for them.
      const grants: ModuleRoleGrant[] = inviteRole === 'member'
        ? grantsFromMatrixValue(inviteMatrix)
        : [];

      const { error: inviteErrorResponse } = await teamService.inviteTeamMember({
        company_id: company.id,
        email: inviteEmail.toLowerCase().trim(),
        role: inviteRole,
        module_role_grants: grants,
      });

      if (inviteErrorResponse) {
        // Handle specific error codes - keep modal open and show error inside
        if (typeof inviteErrorResponse === 'object' && 'code' in inviteErrorResponse) {
          if (inviteErrorResponse.code === 'USER_NOT_FOUND') {
            // User not found - show localized message
            setInviteError(t('team.messages.userNotFound'));
          } else if (inviteErrorResponse.code === 'ALREADY_MEMBER') {
            // Already a member - show localized message
            setInviteError(t('team.messages.alreadyMember'));
          } else {
            // Any other error (RLS, insert error, etc.) - show actual error message
            const errorMsg = inviteErrorResponse.message || t('team.messages.inviteError');
            console.error('Team invite error:', inviteErrorResponse);
            setInviteError(errorMsg);
          }
        } else {
          // Fallback for unexpected error format
          setInviteError(t('team.messages.inviteError'));
        }
        return; // Keep modal open
      }

      // Success - close modal and show success message on page
      setSuccess(t('team.messages.inviteSuccess'));
      setShowInviteModal(false);
      resetInviteForm();
      
      // Reload team members
      await loadData();
    } catch (err) {
      console.error('Invite error:', err);
      setInviteError(t('team.messages.inviteError'));
    } finally {
      setInviting(false);
    }
  };

  const resetInviteForm = () => {
    setInviteEmail('');
    setInviteRole('member');
    setInviteMatrix({});
    setInviteError(null);
  };

  const handleEditMember = async (member: TeamMember) => {
    setSelectedMember(member);
    // Ensure role is valid - convert any invalid roles to 'member'
    setEditRole(member.role === 'admin' ? 'admin' : 'member');

    // Phase 76.2.D — hydrate the matrix from the canonical v76 grant table.
    // On un-migrated members (no v76 rows yet) fall back to the legacy JSON
    // column so the dialog opens with the existing access shape rather than
    // empty cells.
    const { data: v76Grants } = await teamService.getMemberModuleRoles(member.id);
    if (v76Grants && Object.keys(v76Grants).length > 0) {
      setEditMatrix(v76Grants as PermissionsMatrixValue);
    } else {
      const legacy = member.module_permissions ?? {};
      setEditMatrix({
        cashflow:  (legacy.cashflow  ?? 'none') as PermissionsMatrixValue[string],
        hr:        (legacy.hr        ?? 'none') as PermissionsMatrixValue[string],
        inventory: (legacy.inventory ?? 'none') as PermissionsMatrixValue[string],
      });
    }

    setShowEditModal(true);
  };

  const handleUpdateMember = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedMember) return;

    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      // Phase 76.2.D — translate the matrix value into canonical grants.
      const grants: ModuleRoleGrant[] = editRole === 'member'
        ? grantsFromMatrixValue(editMatrix)
        : [];

      const { error: updateError } = await teamService.updateTeamMember(
        selectedMember.id,
        editRole,
        grants,
      );

      if (updateError) {
        setError(t('team.messages.updateError'));
        return;
      }

      setSuccess(t('team.messages.updateSuccess'));
      setShowEditModal(false);
      setSelectedMember(null);
      
      // Reload team members
      await loadData();
    } catch (err) {
      console.error('Update error:', err);
      setError(t('team.messages.updateError'));
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveMember = (member: TeamMember) => {
    setSelectedMember(member);
    setShowRemoveModal(true);
  };

  const confirmRemoveMember = async () => {
    if (!selectedMember) return;

    setRemoving(true);
    setError(null);
    setSuccess(null);

    try {
      const { error: removeError } = await teamService.removeTeamMember(selectedMember.id);

      if (removeError) {
        setError(t('team.messages.removeError'));
        return;
      }

      setSuccess(t('team.messages.removeSuccess'));
      setShowRemoveModal(false);
      setSelectedMember(null);
      
      // Reload team members
      await loadData();
    } catch (err) {
      console.error('Remove error:', err);
      setError(t('team.messages.removeError'));
    } finally {
      setRemoving(false);
    }
  };

  const isCurrentUser = (member: TeamMember) => {
    return member.user_id === user?.id;
  };

  const getPermissionSummary = (member: TeamMember) => {
    const grants = member.module_grants;
    if (!grants || Object.keys(grants).length === 0) return '—';
    const parts = Object.entries(grants).map(([mod, level]) => {
      const modLabel = t(`team.invite.${mod}Module`, { defaultValue: mod });
      const lvlLabel = t(`team.invite.permissionLevels.${level}`, { defaultValue: level });
      return `${modLabel}: ${lvlLabel}`;
    });
    return parts.length > 0 ? parts.join(', ') : '—';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No workspace found</h3>
          <p className="mt-1 text-sm text-gray-500">Please create or join a workspace first.</p>
          <button
            onClick={() => navigate('/workspaces')}
            className="mt-6 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            Go to Workspaces
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button & Header */}
      <div>
        <button
          onClick={() => navigate('/workspaces')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Workspaces</span>
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-1">Company Settings</h1>
        <p className="text-gray-600 mb-6">
          Manage your company information and team members
        </p>
      </div>

      {/* Settings Tabs Navigation */}
      <SettingsTabs />

      {/* Page Content */}
      <div className="pt-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t('team.title')}</h2>
            <p className="mt-1 text-sm text-gray-600">{t('team.subtitle')}</p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            {t('team.inviteButton')}
          </button>
        </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 rounded-md bg-green-50 border border-green-200 p-4">
          <div className="flex">
            <CheckCircle2 className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{success}</p>
            </div>
            <button
              onClick={() => setSuccess(null)}
              className="ml-auto -mx-1.5 -my-1.5 rounded-md p-1.5 text-green-500 hover:bg-green-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-md bg-red-50 border border-red-200 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto -mx-1.5 -my-1.5 rounded-md p-1.5 text-red-500 hover:bg-red-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Team Members Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        {teamMembers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('team.noMembers')}</h3>
            <p className="mt-1 text-sm text-gray-500">{t('team.noMembersDescription')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('team.table.email')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('team.table.role')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('team.table.permissions')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('team.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teamMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Mail className="h-5 w-5 text-gray-400 mr-3" />
                        <div className="text-sm font-medium text-gray-900">
                          {member.email || 'No email'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Shield className="h-4 w-4 text-gray-400 mr-2" />
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          member.role === 'admin' 
                            ? 'bg-purple-100 text-purple-800'
                            : member.role === 'member'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {t(`team.roles.${member.role}`)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">
                        {member.role === 'admin' ? (
                          <span className="text-purple-600 font-medium">Full Access</span>
                        ) : (
                          <div className="space-y-1">
                            {getPermissionSummary(member)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {isCurrentUser(member) ? (
                        <span className="text-gray-400 text-xs">
                          {t('team.actions.cannotRemoveSelf')}
                        </span>
                      ) : (
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleEditMember(member)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <Edit2 className="h-3 w-3 mr-1" />
                            {t('team.actions.edit')}
                          </button>
                          <button
                            onClick={() => handleRemoveMember(member)}
                            className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            {t('team.actions.remove')}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">{t('team.invite.title')}</h3>
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    resetInviteForm();
                    setError(null);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleInvite} className="px-6 py-4 space-y-6">
              {/* Error Alert Inside Modal */}
              {inviteError && (
                <div className="rounded-md bg-red-50 border border-red-200 p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800">{inviteError}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setInviteError(null)}
                      className="ml-auto -mx-1.5 -my-1.5 rounded-md p-1.5 text-red-500 hover:bg-red-100"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('team.invite.email')}
                </label>
                <input
                  type="email"
                  id="email"
                  value={inviteEmail}
                  onChange={(e) => {
                    setInviteEmail(e.target.value);
                    setInviteError(null); // Clear error when user starts typing
                  }}
                  placeholder={t('team.invite.emailPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">{t('team.invite.emailHint')}</p>
              </div>

              {/* Global Role */}
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('team.invite.globalRole')}
                </label>
                <select
                  id="role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="admin">{t('team.roles.admin')}</option>
                  <option value="member">{t('team.roles.member')}</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">{t('team.invite.globalRoleHint')}</p>
              </div>

              {/* Module Permissions (only for members) — Phase 76.2.C dynamic matrix */}
              {inviteRole === 'member' && (
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-1">
                    {t('team.invite.permissionsTitle')}
                  </h4>

                  <PermissionsMatrix
                    companyId={activeCompanyId}
                    disabled={inviting}
                    value={inviteMatrix}
                    onChange={setInviteMatrix}
                  />
                </div>
              )}

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteModal(false);
                    resetInviteForm();
                    setError(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t('team.invite.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={inviting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                >
                  {inviting ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      {t('team.invite.submitting')}
                    </>
                  ) : (
                    t('team.invite.submit')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {showEditModal && selectedMember && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">{t('team.edit.title')}</h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedMember(null);
                    setError(null);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleUpdateMember} className="px-6 py-4 space-y-6">
              {/* Email (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('team.invite.email')}
                </label>
                <input
                  type="email"
                  value={selectedMember.email || ''}
                  disabled
                  className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
                />
              </div>

              {/* Global Role */}
              <div>
                <label htmlFor="edit-role" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('team.edit.globalRole')}
                </label>
                <select
                  id="edit-role"
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value as 'admin' | 'member')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="admin">{t('team.roles.admin')}</option>
                  <option value="member">{t('team.roles.member')}</option>
                </select>
              </div>

              {/* Module Permissions (only for members) — Phase 76.2.C dynamic matrix */}
              {editRole === 'member' && (
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-1">
                    {t('team.edit.permissionsTitle')}
                  </h4>

                  <PermissionsMatrix
                    companyId={activeCompanyId}
                    disabled={updating}
                    value={editMatrix}
                    onChange={setEditMatrix}
                  />
                </div>
              )}

              {/* Form Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedMember(null);
                    setError(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t('team.edit.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={updating}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                >
                  {updating ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      {t('team.edit.submitting')}
                    </>
                  ) : (
                    t('team.edit.submit')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Remove Member Confirmation Modal */}
      {showRemoveModal && selectedMember && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserX className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="ml-3 text-lg font-medium text-gray-900">{t('team.remove.title')}</h3>
              </div>
            </div>

            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 mb-4">
                {t('team.remove.message')}
              </p>
              <div className="bg-gray-50 rounded-md p-3 mb-4">
                <div className="flex items-center">
                  <Mail className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-900">{selectedMember.email}</span>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowRemoveModal(false);
                  setSelectedMember(null);
                }}
                disabled={removing}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('team.remove.cancel')}
              </button>
              <button
                onClick={confirmRemoveMember}
                disabled={removing}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
              >
                {removing ? (
                  <>
                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                    Removing...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('team.remove.confirm')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
